import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link } from "react-router-dom";
import { Button, InlineNotification, Loading } from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import SelectField, { SelectFieldOption } from "../carbon/Form/SelectField";
import PhoneField from "../carbon/Form/PhoneField";
import PageHeader from "../carbon/PageHeader/PageHeader";
import RolePicker from "../carbon/RolePicker/RolePicker";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import {
  editUserValidationSchema,
  EditUserFormValues,
} from "./editUserValidationSchema";
import {
  ABILITIES,
  USER_COMPANY_LIST_FOR_BUYER_ADMIN,
  USER_COMPANY_LIST_FOR_BUYER_MANAGER,
  USER_COMPANY_LIST_FOR_BUYER,
  USER_COMPANY_LIST_FOR_SELLER_ADMIN,
  USER_COMPANY_LIST_FOR_SELLER_MANAGER,
  USER_COMPANY_LIST_FOR_SELLER,
  USER_COMPANY_TYPES,
} from "../../utils";
import { UserCompanyDetail } from "../../types";
import { useUser } from "../../context";
import "./editUserForm.scss";

// The same role lookup tables NewUserForm.tsx builds for its own Role select, duplicated rather
// than shared: the pre-Carbon EditUserForm and NewUserForm each carried their own copy of the
// equivalent JSX conditionals, and nothing in this task asks the two flows to start sharing a
// module.
const BUYER_ROLE_OPTIONS: Record<
  string,
  typeof USER_COMPANY_LIST_FOR_BUYER_ADMIN
> = {
  [USER_COMPANY_TYPES.ADMIN]: USER_COMPANY_LIST_FOR_BUYER_ADMIN,
  [USER_COMPANY_TYPES.MANAGER]: USER_COMPANY_LIST_FOR_BUYER_MANAGER,
  [USER_COMPANY_TYPES.BUYER]: USER_COMPANY_LIST_FOR_BUYER,
};

const SELLER_ROLE_OPTIONS: Record<
  string,
  typeof USER_COMPANY_LIST_FOR_BUYER_ADMIN
> = {
  [USER_COMPANY_TYPES.ADMIN]: USER_COMPANY_LIST_FOR_SELLER_ADMIN,
  [USER_COMPANY_TYPES.MANAGER]: USER_COMPANY_LIST_FOR_SELLER_MANAGER,
  [USER_COMPANY_TYPES.COMMERCIAL]: USER_COMPANY_LIST_FOR_SELLER,
};

// The Responsible <select>'s sentinel for "this person has no Responsible" — not SelectField's
// own `placeholder` mechanism (this field never passes one; an edit form is always pre-filled
// with a definite value, either a colleague's id or this). The backend's contract for `parent`
// (see this task's brief): `{"id": "<uuid>"}` reassigns, `{"id": null}` clears, and omitting the
// key entirely leaves it untouched. This form always sends the key — every submit here is an
// explicit reassert or an explicit clear, never a "leave untouched".
export const NO_RESPONSIBLE_VALUE = "";

type EditUserFormProps = {
  userToEdit?: UserCompanyDetail;
  /** `roleIds` is present only when this viewer may assign RBAC roles (`canAssignRoles`): the
   * full set to apply after the user PUT succeeds. The caller (EditUser.tsx) owns the
   * update-then-assign sequencing. */
  onEditUser: ({
    userId,
    body,
    roleIds,
  }: {
    userId: string;
    body: any;
    roleIds?: string[];
  }) => void;
  error: boolean;
  usersByCompany?: UserCompanyDetail[];
  /** The edit mutation's own `isPending`, threaded through to `ConfirmDialog` so it can show an
   * inline spinner on its confirm button instead of the whole page disappearing behind a
   * full-screen loader mid-confirmation. */
  isPending?: boolean;
};

/**
 * The edit-user form (`PUT /user/{id}`), rebuilt on the Carbon form primitives (Task 12) in
 * place of 303 lines of Tailwind and hand-rolled RHF `register` wiring — the same rebuild
 * `NewUserForm.tsx` (Task 11) already went through, and this form's own template.
 *
 * Two defects the pre-Carbon version carried, both closed here rather than carried forward:
 *
 * 1. **The Role select rendered even for a caller editing their own profile.** Task 3 put an
 *    authorization guard behind `PUT /user/{id}` that silently ignores a self-submitted
 *    `userCompanyType` — self-escalation is how an authorization mistake becomes unrecoverable.
 *    Rendering the control anyway would be a lie about what it does. `canManageThisEdit` hides
 *    it (and `editUserValidationSchema` stops requiring it in that case — see that module).
 *    **Task 14's fix round extended this**: the same guard (`UserMutationGuard`) also rejects
 *    a role/parent change from a viewer who is editing someone else but isn't an admin or
 *    manager, and until this fix the form didn't know that — a Buyer or Commercial colleague
 *    could see both selects, submit, get a success toast, and have the write silently dropped
 *    server-side. `canManageThisEdit` is `!isEditingSelf && canManagePeople` now, not just
 *    `!isEditingSelf`.
 *
 * 2. **`parent` had two sources of truth.** The pre-Carbon form kept both an RHF-registered
 *    `parent` field (a bare id string) and a separate `parentId` `useState`, overriding with the
 *    correct `{ id }` shape only when `parentId` was truthy — the other branch let the bare
 *    string from `...data` through untouched, and `PUT /user/{id}` 400s on it. This version has
 *    exactly one source, the RHF-controlled `parent` field below, always reshaped to the nested
 *    form the backend expects in `handleConfirm`.
 *
 * A third gap this task closes, not a bug it's fixing: there was no way to *clear* a Responsible
 * once set — no "no Responsible" option existed in the select at all — even though the backend
 * already accepts the clear signal. `NO_RESPONSIBLE_VALUE` is that option.
 */
const EditUserForm = ({
  error,
  onEditUser,
  userToEdit,
  usersByCompany,
  isPending = false,
}: EditUserFormProps) => {
  const { user, isBuy, isSeller, canManagePeople, can } = useUser();
  const { t } = useTranslation();

  // Whether the record being edited IS the logged-in caller — the same identity check the
  // backend guard makes, mirrored here so the UI never offers a control the API will silently
  // ignore.
  const isEditingSelf = !!user && !!userToEdit && user.id === userToEdit.id;

  // Whether this viewer may actually change the target's role or Responsible at all —
  // mirrors `UserService.updateUser`'s `mayChangeRole`, which UserController now derives from
  // the Tier-2 ability (`UserMutationGuard.hasTier2`, i.e. `ADMINISTER_USERS` /
  // `SELLER_ADMINISTER_USERS`) and which gates BOTH `userCompanyType` and `parent` in that one
  // PUT. `canManagePeople` (UserProvider) is the same ability check, so the control matches the
  // server. Before this, the Role select was hidden only for a self-edit, so
  // a viewer who cannot manage anyone's role at all — e.g. a Buyer or Commercial colleague —
  // still saw it: an edit "succeeded" with a success toast, the role visibly (and, for that
  // viewer's restricted option list, sometimes *wrongly*) selected, and the backend silently
  // dropped the write underneath it. Same story for Responsible, gated by the identical
  // `mayChangeRole` on the backend side.
  const canManageThisEdit = !isEditingSelf && canManagePeople;

  // Assigning RBAC roles is a separate write (`PUT /user/{id}/roles`, MANAGE_USERS server-side)
  // and, like the role/parent change above, never offered on one's own record.
  const canAssignRoles =
    !isEditingSelf &&
    (can(ABILITIES.MANAGE_USERS) || can(ABILITIES.SELLER_MANAGE_USERS));

  const defaultValues: EditUserFormValues = useMemo(
    () => ({
      firstName: userToEdit?.firstName ?? "",
      lastName: userToEdit?.lastName ?? "",
      phone: userToEdit?.phone ?? "",
      userCompanyType: userToEdit?.userCompanyType ?? "",
      parent: userToEdit?.parent?.id ?? NO_RESPONSIBLE_VALUE,
    }),
    [userToEdit]
  );

  // Seeded from the user's CURRENT roles, so an untouched save re-sends the same set instead of
  // wiping them (the endpoint replaces the whole set).
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(
    () => userToEdit?.roles?.map((role) => role.id) ?? []
  );

  // Roles are written only when the picker actually differs from what the user came in with.
  // The endpoint replaces the whole set, and a user that arrives WITHOUT `roles` (older BE,
  // stale cache) seeds as [] — an unconditional write would silently wipe their roles on an
  // unrelated edit (e.g. a phone change).
  const seededRoleIds = useMemo(
    () => (userToEdit?.roles ?? []).map((role) => role.id),
    [userToEdit]
  );
  const rolesChanged =
    [...selectedRoleIds].sort().join() !== [...seededRoleIds].sort().join();

  const [pendingValues, setPendingValues] = useState<EditUserFormValues | null>(
    null
  );

  const { control, handleSubmit, reset } = useForm<EditUserFormValues>({
    resolver: yupResolver(editUserValidationSchema(canManageThisEdit)),
    defaultValues,
  });

  // `userToEdit` arrives from EditUser.tsx's own fetch — `useForm`'s `defaultValues` only seeds
  // the form on the very first render, so this re-syncs the form if the fetched record shows up
  // (or changes) after mount. A defensive no-op in the normal case (EditUser.tsx already gates
  // rendering behind its own loading state, so `userToEdit` is populated before this component
  // ever mounts), but this component takes `userToEdit` as a plain prop and shouldn't assume
  // every caller (a unit test included) provides that guarantee.
  useEffect(() => {
    reset(defaultValues);
    setSelectedRoleIds(userToEdit?.roles?.map((role) => role.id) ?? []);
  }, [defaultValues, reset, userToEdit]);

  // The mutation lives in EditUser.tsx (`useUpdateUser`), not here, so this form only learns
  // about a failed submit through the `error` prop flipping true. Closing the dialog then mirrors
  // the pre-Carbon form's `setOpen(false)` right after calling `onEditUser` — the difference is
  // this fires only once the outcome is actually known, not optimistically before it.
  useEffect(() => {
    if (error) {
      setPendingValues(null);
    }
  }, [error]);

  const roleOptions: SelectFieldOption[] = useMemo(() => {
    const table = isBuy
      ? BUYER_ROLE_OPTIONS
      : isSeller
        ? SELLER_ROLE_OPTIONS
        : {};
    const list = user?.userCompanyType
      ? table[user.userCompanyType]
      : undefined;

    return (list ?? []).map((option) => ({
      value: option.type,
      label: option.name,
    }));
  }, [isBuy, isSeller, user?.userCompanyType]);

  const parentOptions: SelectFieldOption[] = useMemo(() => {
    const colleagues = (usersByCompany ?? [])
      // Belt-and-suspenders against the person being edited appearing as their own Responsible —
      // EditUser.tsx's `useUsersByCompany(id)` already filters the target out server-side, but
      // this form doesn't assume every caller supplies an already-filtered list.
      .filter((colleague) => colleague.id !== userToEdit?.id)
      .map((colleague) => ({
        value: colleague.id,
        label: colleague.fullName,
      }));
    // The pre-Carbon form always appended the caller themself as a pickable option too — carried
    // over unchanged.
    const withCaller = user
      ? [...colleagues, { value: user.id, label: user.fullName }]
      : colleagues;

    // De-duplicated by id, first occurrence winning. `GET /user/company` returns the company's
    // whole active roster and excludes nobody — the caller included (UserController#
    // getAllUsersByCompany -> UserService#findByCompany -> findByCompany_IdAndActiveIsTrue) —
    // and `useUsersByCompany(id)` filters out only the record being EDITED. So on every render
    // where this select appears at all (`canManageThisEdit` is false for a self-edit, which is
    // the one case the hook's filter would have removed the caller) the append above is a
    // second copy of someone already in `colleagues`: two options with the same `value`, which
    // SelectField hands to `SelectItem` as the same React `key`, which is React's "Encountered
    // two children with the same key" warning on /users/:id/edit.
    //
    // Deduping rather than dropping the append: the roster is scoped to the caller's *current*
    // company (UserPrincipal's companyId, switchable via GET /user/company/{companyId}), so it
    // isn't guaranteed to contain them, and silently removing the caller from their own
    // Responsible picker would be the worse bug. Doing it here rather than in
    // `useUsersByCompany` also covers a roster that repeats any other id, and keeps the hook's
    // other callers untouched.
    const seen = new Set<string>();
    const uniqueOptions = withCaller.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });

    return [
      { value: NO_RESPONSIBLE_VALUE, label: t("users.noResponsible") },
      ...uniqueOptions,
    ];
  }, [usersByCompany, user, userToEdit?.id, t]);

  const handleConfirm = () => {
    if (!pendingValues || !userToEdit) return;

    const body: Record<string, unknown> = {
      firstName: pendingValues.firstName,
      lastName: pendingValues.lastName,
      phone: pendingValues.phone,
      company: { id: userToEdit.company.id },
    };
    // Both omitted entirely, not resubmitted unchanged, when Role/Responsible aren't rendered —
    // a field the caller never saw shouldn't appear in the request as something they submitted.
    // `PUT /user/{id}` silently ignores both for a caller `mayChangeRole` doesn't cover anyway
    // (Task 3's guard, and UserService.updateUser's `applyParent` is behind the same flag), but
    // the payload should say what the caller actually did.
    if (canManageThisEdit) {
      body.userCompanyType = pendingValues.userCompanyType;
      // Nested shape — the fix Task 12 exists to make (see this component's docstring, point
      // 2). The sentinel maps to the backend's clear signal; any other value is a colleague's
      // (or the caller's own) real id.
      body.parent = {
        id:
          pendingValues.parent === NO_RESPONSIBLE_VALUE
            ? null
            : pendingValues.parent,
      };
    }

    onEditUser({
      userId: userToEdit.id,
      body,
      ...(canAssignRoles && rolesChanged ? { roleIds: selectedRoleIds } : {}),
    });
  };

  return (
    <div>
      <PageHeader title={t("users.edit")} />

      {error ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("users.emailExists")}
          className="edit-user-form__notification"
        />
      ) : null}

      {!userToEdit ? (
        <Loading withOverlay={false} description={t("cta.loading")} small />
      ) : (
        <Form
          handleSubmit={handleSubmit}
          onSubmit={(data) => setPendingValues(data)}
          noValidate
          className="edit-user-form cds-form-surface"
        >
          <div className="edit-user-form__grid">
            <TextField
              name="firstName"
              label={t("users.firstName")}
              control={control}
              required
            />
            <TextField
              name="lastName"
              label={t("users.lastName")}
              control={control}
              required
            />
            <PhoneField
              name="phone"
              label={t("users.phone")}
              countryCodeLabel={t("users.phoneCountryCode")}
              control={control}
              required
            />
            {/* Hidden for a self-edit (this component's docstring, point 1) AND for a viewer
                who cannot manage anyone's role at all — see `canManageThisEdit` above. */}
            {canManageThisEdit && (
              <SelectField
                name="userCompanyType"
                label={t("users.role")}
                control={control}
                options={roleOptions}
                required
              />
            )}
            {canAssignRoles && (
              <RolePicker
                selectedIds={selectedRoleIds}
                onChange={setSelectedRoleIds}
                labelText={t("users.roles")}
              />
            )}
            {canManageThisEdit && (
              <SelectField
                name="parent"
                label={t("users.responsible")}
                control={control}
                options={parentOptions}
                optional
              />
            )}
          </div>

          <div className="edit-user-form__actions">
            <Button as={Link} to="/users" kind="secondary">
              {t("cta.cancel")}
            </Button>
            <Button type="submit">{t("cta.edit")}</Button>
          </div>
        </Form>
      )}

      <ConfirmDialog
        open={pendingValues !== null}
        title={t("users.edit")}
        body={t("users.editQuestion")}
        confirmLabel={t("cta.edit")}
        onConfirm={handleConfirm}
        onCancel={() => setPendingValues(null)}
        isPending={isPending}
      />
    </div>
  );
};

export default EditUserForm;
